import { useEffect, useState } from "react";
import { Pencil, Trash, Plus, DollarSignIcon } from "lucide-react";
import PaymentMethodForm from "../Components/Payment/PaymentMethodForm";
import { FaBitcoin, FaMobileAlt, FaPaypal, FaUniversity } from "react-icons/fa";
import { SiPayoneer } from "react-icons/si";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { useAuth } from "../Auth/AuthContext";
import paymentMethodsService from "../Services/paymentMethods";

export default function PublisherPayments() {
    const { user } = useAuth();
    const [methods, setMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [openModal, setOpenModal] = useState(false);

    useEffect(() => {
        if (!user?.publisherId) return;

        const loadMethods = async () => {
            try {
                const res = await paymentMethodsService.getByPublisher(user.publisherId);
                setMethods(res);
            } catch (error) {
                toast.error("Failed to load payment methods");
            } finally {
                setLoading(false);
            }
        };

        loadMethods();
    }, [user?.publisherId]);

    const handleSave = async (method) => {
        if (!method.method) {
            toast.error("Please select a payment method.");
            return;
        }

        // 📌 Validation by method
        switch (method.method) {
            case "Paypal":
            case "Payoneer":
                if (!method.details || method.details.length < 5 || method.details.length > 100) {
                    toast.error(`${method.method} email must be between 5 and 100 characters.`);
                    return;
                }
                break;

            case "BTC":
                if (!method.details || method.details.length < 25 || method.details.length > 64) {
                    toast.error("BTC address must be between 25 and 64 characters.");
                    return;
                }
                break;

            case "UPI":
                if (!method.details || method.details.length < 5 || method.details.length > 50) {
                    toast.error("UPI ID must be between 5 and 50 characters.");
                    return;
                }

                const upiRegex = /^[\w.-]{2,256}@[A-Za-z]{2,64}$/;
                if (!upiRegex.test(method.details)) {
                    toast.error("Invalid UPI ID format. Example: username@bank");
                    return;
                }
                break;

            case "Bank/Wire Transfer":
                if (!method.bankName?.trim()) {
                    toast.error("Bank name is required.");
                    return;
                }
                if (!method.accountName?.trim()) {
                    toast.error("Account name is required.");
                    return;
                }
                if (!method.accountNumber?.trim()) {
                    toast.error("Account number is required.");
                    return;
                }
                if (!method.swiftCode && !method.iban) {
                    toast.error("Please provide SWIFT or IBAN.");
                    return;
                }
                break;

            default:
                toast.error("Unknown payment method.");
                return;
        }

        console.log("submitted method :", method);

        // 📦 Payload for backend
        const payload = {
            method: method.method === "Bank/Wire Transfer" ? "Bank" : method.method,

            // Email/crypto/UPI fields
            paypalEmail: method.method === "Paypal" ? method.details : null,
            payoneerEmail: method.method === "Payoneer" ? method.details : null,
            btcAddress: method.method === "BTC" ? method.details : null,
            upiId: method.method === "UPI" ? method.details : null,

            // Bank fields
            bankName: method.method === "Bank/Wire Transfer" ? method.bankName : null,
            accountName: method.method === "Bank/Wire Transfer" ? method.accountName : null,
            accountNumber: method.method === "Bank/Wire Transfer" ? method.accountNumber : null,
            swiftCode: method.method === "Bank/Wire Transfer" ? method.swiftCode : null,
            ibanCode: method.method === "Bank/Wire Transfer" ? method.iban : null,
            bankAddress: method.method === "Bank/Wire Transfer" ? method.bankAddress : null,
            achRoutingNumber: method.method === "Bank/Wire Transfer" ? method.achRoutingNo : null,
            fedwireNumber: method.method === "Bank/Wire Transfer" ? method.fedwireNo : null,

            // More bank fields
            postalCode: method.method === "Bank/Wire Transfer" ? method.postalCode : null,
            city: method.method === "Bank/Wire Transfer" ? method.city : null,
            country: method.method === "Bank/Wire Transfer" ? method.country : null,
            branchCode: method.method === "Bank/Wire Transfer" ? method.branchCode : null,

            // Common fields
            isActive: editing ? editing.isActive : methods.length === 0,
            verified: editing ? editing.verified ?? false : false,
            publisherId: user.publisherId,
        };

        console.log("Payload :", payload);

        try {
            let savedMethod;

            if (editing) {
                await paymentMethodsService.update(editing.id, payload);
                toast.success("Payment method updated successfully");
                savedMethod = { id: editing.id, ...payload };
            } else {
                const created = await paymentMethodsService.create(payload);
                toast.success("Payment method added successfully");
                savedMethod = { id: created.id, ...payload };
            }

            setMethods((prev) =>
                editing
                    ? prev.map((m) => (m.id === editing.id ? savedMethod : m))
                    : [...prev, savedMethod]
            );

            setEditing(null);
            setOpenModal(false);
        } catch (err) {
            console.error("❌ Save error:", err);
            toast.error("Failed to save payment method. Please try again.");
        }
    };



    const handleDelete = async (id) => {
        try {
            await paymentMethodsService.delete(id);
            setMethods((prev) => prev.filter((m) => m.id !== id));
            toast.success("Payment method removed");
        } catch {
            toast.error("Failed to delete method");
        }
    };

    const handleSetActive = async (id) => {
        try {
            const updated = methods.map((m) => ({
                ...m,
                isActive: m.id === id,
            }));

            await Promise.all(
                updated.map((m) =>
                    paymentMethodsService.update(m.id, { ...m, publisherId: user.publisherId })
                )
            );

            setMethods(updated);
            toast.success("Active payment method updated");
        } catch {
            toast.error("Failed to update active method");
        }
    };

    const handleEdit = (method) => {
        setEditing({
            id: method.id,
            method: method.method === "Bank" ? "Bank/Wire Transfer" : method.method,
            bankName: method.bankName || "",
            accountName: method.accountName || "",
            accountNumber: method.accountNumber || "",
            swiftCode: method.swiftCode || "",
            ibanCode: method.ibanCode || "",
            bankAddress: method.bankAddress || "",
            postalCode: method.postalCode || "",
            city: method.city || "",
            country: method.country || "",
            branchCode: method.branchCode || "",
            achRoutingNumber: method.achRoutingNumber || "",
            fedwireNumber: method.fedwireNumber || "",
            details:
                method.paypalEmail || method.payoneerEmail || method.btcAddress || "",
            isActive: method.isActive || false,
            verified: method.verified || false,
        });
        setOpenModal(true);
    };


    return (
        <div className="max-w-6xl mx-auto space-y-8 p-4 sm:p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <motion.h2
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="text-xl md:text-2xl lg:text-3xl font-extrabold flex items-center gap-2 text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"
                    >
                        <DollarSignIcon className="w-6 h-6 text-pink-500 animate-bounce-slow" />
                        Payment Methods
                    </motion.h2>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">
                        Manage how you receive payments from your content
                    </p>
                </div>

                <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setOpenModal(true)}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all"
                >
                    <Plus size={18} />
                    Add Payment Method
                </motion.button>
            </div>

            {/* Payment Cards */}
            <div className="bg-white dark:bg-gray-800/50 w-full max-w-6xl p-6 sm:p-8 rounded-3xl border border-gray-200 dark:border-gray-700">
                {methods.length > 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                        {methods.map((method) => (
                            <motion.div
                                key={method.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3 }}
                                className={`rounded-xl border p-5 transition-all ${method.isActive
                                    ? "border-blue-500/30 bg-blue-50/50 dark:bg-blue-900/10"
                                    : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700/30"
                                    }`}
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className={`p-3 rounded-lg ${method.method === "BTC"
                                                ? "bg-orange-500/10 text-orange-500"
                                                : method.method === "Paypal"
                                                    ? "bg-blue-500/10 text-blue-500"
                                                    : method.method === "Payoneer"
                                                        ? "bg-red-500/10 text-red-500"
                                                        : "bg-gray-500/10 text-gray-500"
                                                }`}
                                        >
                                            {method.method === "BTC" && <FaBitcoin size={20} />}
                                            {method.method === "Paypal" && <FaPaypal size={20} />}
                                            {method.method === "Payoneer" && <SiPayoneer size={20} />}
                                            {method.method === "Bank" && <FaUniversity size={20} />}
                                            {method.method === "UPI" && <FaMobileAlt size={20} />}
                                        </div>
                                        <div>
                                            <h3 className="font-medium text-gray-800 dark:text-gray-100">
                                                {method.method === "Bank" ? "Bank/Wire Transfer" : method.method}
                                            </h3>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {method.method === "Bank"
                                                    ? method.bankName
                                                    : method.method === "Paypal"
                                                        ? method.paypalEmail
                                                        : method.method === "Payoneer"
                                                            ? method.payoneerEmail
                                                            : method.method === "BTC"
                                                                ? method.btcAddress?.slice(0, 12) + "..."
                                                                : method.method === "UPI"
                                                                    ? method.upiId
                                                                    : ""
                                                }

                                            </p>
                                        </div>
                                    </div>

                                    {method.isActive ? (
                                        <span className="px-2.5 py-1 text-xs font-medium bg-green-500/10 text-green-700 dark:text-green-400 rounded-full">
                                            Active
                                        </span>
                                    ) : (
                                        <button
                                            onClick={() => handleSetActive(method.id)}
                                            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                                        >
                                            Set Active
                                        </button>
                                    )}
                                </div>

                                {method.method === "Bank" && (
                                    <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                                        <p><strong>Bank Name:</strong> {method.bankName || "—"}</p>
                                        <p><strong>Account Name:</strong> {method.accountName || "—"}</p>
                                        <p><strong>Account Number:</strong> {method.accountNumber || "—"}</p>
                                        {method.swiftCode && <p><strong>SWIFT:</strong> {method.swiftCode}</p>}
                                        {method.ibanCode && <p><strong>IBAN:</strong> {method.ibanCode}</p>}
                                        {method.bankAddress && <p><strong>Bank Address:</strong> {method.bankAddress}</p>}
                                        {method.branchCode && <p><strong>Branch Code:</strong> {method.branchCode}</p>}
                                        {method.city && <p><strong>City:</strong> {method.city}</p>}
                                        {method.postalCode && <p><strong>Postal Code:</strong> {method.postalCode}</p>}
                                        {method.country && <p><strong>Country:</strong> {method.country}</p>}
                                        {method.achRoutingNumber && <p><strong>ACH Routing No:</strong> {method.achRoutingNumber}</p>}
                                        {method.fedwireNumber && <p><strong>Fedwire No:</strong> {method.fedwireNumber}</p>}
                                    </div>
                                )}
                                {method.method === "UPI" && (
                                    <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                                        <p><strong>UPI ID:</strong> {method.upiId || "—"}</p>
                                    </div>
                                )}

                                <div className="pt-4 flex justify-end gap-3">
                                    {/* <button
                                        onClick={() => handleEdit(method)}
                                        className="p-2 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 rounded-full"
                                    >
                                        <Pencil size={16} />
                                    </button> */}
                                    <button
                                        onClick={() => handleDelete(method.id)}
                                        className="p-2 text-gray-500 hover:text-red-600 dark:hover:text-red-400 rounded-full"
                                    >
                                        <Trash size={16} />
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                        No payment methods yet.
                        <div>
                            <button
                                onClick={() => setOpenModal(true)}
                                className="mt-3 text-blue-600 dark:text-blue-400 font-medium hover:underline"
                            >
                                + Add Payment Method
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Payment Form Modal */}
            <AnimatePresence>
                {openModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
                    >
                        <motion.div
                            initial={{ scale: 0.95, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 20 }}
                            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-md"
                        >
                            <div className="flex justify-between items-center p-5 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {editing ? "Edit Payment Method" : "Add Payment Method"}
                                </h3>
                                <button
                                    onClick={() => {
                                        setOpenModal(false);
                                        setEditing(null);
                                    }}
                                    className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="p-5 overflow-y-auto max-h-[80vh]">
                                <PaymentMethodForm
                                    onSubmit={handleSave}
                                    onCancel={() => {
                                        setOpenModal(false);
                                        setEditing(null);
                                    }}
                                    initialData={editing}
                                />
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
